import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Plus, AlertCircle, RefreshCw, Check, X as XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { useAccount } from '@/contexts/AccountContext'
import { useAuth, PERMISSIONS } from '@/contexts/AuthContext'
import { useUsageAdjustments } from '../queries/usageAdjustments'
import { CreateAdjustmentModal } from '../components/CreateAdjustmentModal'
import { DecisionModal } from '../components/DecisionModal'
import type { AdjustmentStatus, AdjustmentDirection, AdjustmentDecisionAction, UsageAdjustmentRequest } from '../types/usageAdjustment.types'

const STATUS_BADGE: Record<AdjustmentStatus, { label: string; variant: 'warning' | 'success' | 'destructive' }> = {
  pending_approval: { label: 'Pending Approval', variant: 'warning' },
  approved: { label: 'Approved', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'destructive' },
}

const DIRECTION_BADGE: Record<AdjustmentDirection, { label: string; variant: 'info' | 'secondary' }> = {
  INGRESS: { label: 'Ingress', variant: 'info' },
  OUTGRESS: { label: 'Outgress', variant: 'secondary' },
}

const CATEGORY_LABEL: Record<string, string> = {
  CAMPAIGN_EVENTS_HISTORICAL: 'Campaign Events Historical',
  WEBSITE_EVENTS_HISTORICAL: 'Website Events Historical',
  DUPLICATE_OFFSET: 'Duplicate Offset',
  HISTORICAL_BACKFILL_PRE_ONBOARDING: 'Historical Backfill (Pre-Onboarding)',
  INCORRECT_EVENT_DATA: 'Incorrect Event Data Correction',
  REPROCESSING_DUPLICATE_OFFSET: 'Reprocessing Double-Count Offset',
  DATA_EXPORT_HISTORICAL: 'Data Export Historical',
  CAMPAIGN_SEND_HISTORICAL: 'Campaign Send Historical',
  OTHER: 'Other',
}

function formatPeriod(start: string, end: string): string {
  if (start === end) return start
  return `${start} → ${end}`
}

export function UsageAdjustmentsPage() {
  const { selectedAccountId } = useAccount()
  const { user, hasPermission } = useAuth()
  const canApprove = hasPermission(PERMISSIONS.USAGE_ADJUSTMENTS_APPROVE)
  const [showCreate, setShowCreate] = useState(false)
  const [decision, setDecision] = useState<{ adjustment: UsageAdjustmentRequest; action: AdjustmentDecisionAction } | null>(null)

  const accountId = selectedAccountId && selectedAccountId !== 'ALL' ? selectedAccountId : null
  const requestedBy = user?.username ?? user?.email ?? 'unknown'

  const { data: adjustments = [], isLoading, isError, error, refetch, isFetching } = useUsageAdjustments(accountId)

  const pageHeader = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Link to="/finance" className="text-gray-400 hover:text-gray-600 transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Adjustments</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Corrections to historical usage measures — submitted for approval, not applied immediately.
          </p>
        </div>
      </div>
      {accountId && (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New Adjustment
          </Button>
        </div>
      )}
    </div>
  )

  if (!accountId) {
    return (
      <div className="p-6 space-y-6">
        {pageHeader}
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 mx-auto mb-4">
            <AlertCircle className="h-7 w-7 text-amber-600" />
          </div>
          <p className="text-gray-600">Select an account to view or submit usage adjustments.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {pageHeader}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Adjustment Requests — Account {accountId}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8">
              <LoadingState message="Loading usage adjustments…" />
            </div>
          ) : isError ? (
            <div className="p-8">
              <ErrorState message={(error as Error).message} onRetry={refetch} />
            </div>
          ) : adjustments.length === 0 ? (
            <div className="p-12 text-center text-gray-500 text-sm">
              No usage adjustments yet for this account.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Requested</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Direction</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Category</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Period</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Event Count</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Reason</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Requested By</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustments.map((adj, idx) => (
                    <tr
                      key={adj.id}
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/50'}`}
                    >
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(adj.requestedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={DIRECTION_BADGE[adj.direction].variant}>{DIRECTION_BADGE[adj.direction].label}</Badge>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                        {CATEGORY_LABEL[adj.category] ?? adj.category}
                      </td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatPeriod(adj.targetPeriodStart, adj.targetPeriodEnd)}</td>
                      <td className={`px-4 py-3 text-right font-mono whitespace-nowrap ${adj.eventCount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {adj.eventCount >= 0 ? '+' : ''}{adj.eventCount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={adj.reason}>
                        {adj.reason}
                        {adj.ticketRef && <span className="text-gray-400 ml-1">({adj.ticketRef})</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{adj.requestedBy}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={STATUS_BADGE[adj.status].variant}>{STATUS_BADGE[adj.status].label}</Badge>
                      </td>
                      <td className="px-4 py-3 min-w-[160px]">
                        {adj.status === 'pending_approval' && canApprove ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="default"
                              className="h-7 px-2 text-xs gap-1"
                              onClick={() => setDecision({ adjustment: adj, action: 'approve' })}
                            >
                              <Check className="h-3 w-3" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="h-7 px-2 text-xs gap-1"
                              onClick={() => setDecision({ adjustment: adj, action: 'reject' })}
                            >
                              <XIcon className="h-3 w-3" /> Reject
                            </Button>
                          </div>
                        ) : adj.status !== 'pending_approval' ? (
                          <div className="text-xs text-gray-500 max-w-xs">
                            <div className="truncate" title={adj.decisionReason}>{adj.decisionReason}</div>
                            <div className="text-gray-400 mt-0.5">
                              {adj.approvedBy}
                              {adj.approvedAt && ' · ' + new Date(adj.approvedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {showCreate && (
        <CreateAdjustmentModal
          accountId={accountId}
          requestedBy={requestedBy}
          onClose={() => setShowCreate(false)}
        />
      )}

      {decision && accountId && (
        <DecisionModal
          accountId={accountId}
          adjustment={decision.adjustment}
          action={decision.action}
          onClose={() => setDecision(null)}
        />
      )}
    </div>
  )
}
